import React, { useState } from 'react';

interface CopyButtonProps {
  text: string;
  className?: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({ text, className = "" }) => {
  const [copied, setCopied] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setIsAnimating(true);
      setCopied(true);
      
      // Reset after animation
      setTimeout(() => {
        setCopied(false);
        setIsAnimating(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`
        relative p-2 rounded-lg transition-all duration-300 transform
        ${copied 
          ? 'bg-[#02C076] text-white scale-110 shadow-lg' 
          : 'bg-[#2B3139] text-[#848E9C] hover:bg-[#F0B90B] hover:text-[#1E2329] hover:scale-105'
        }
        ${isAnimating ? 'animate-bounce' : ''}
        ${className}
      `}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {/* Icon container with animation */}
      <div className="relative w-4 h-4">
        {/* Copy icon */}
        <svg 
          className={`
            absolute inset-0 w-4 h-4 transition-all duration-300 transform
            ${copied ? 'opacity-0 scale-0 rotate-180' : 'opacity-100 scale-100 rotate-0'}
          `} 
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
          <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
        </svg>
        
        {/* Check icon */}
        <svg 
          className={`
            absolute inset-0 w-4 h-4 transition-all duration-300 transform
            ${copied ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-0 -rotate-180'}
          `} 
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </div>
      
      {/* Tooltip */}
      <div className={`
        absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 
        bg-[#1E2329] text-[#FFFFFF] text-xs rounded shadow-lg
        transition-all duration-300 pointer-events-none
        ${copied ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
      `}>
        {copied ? 'Copied!' : 'Copy'}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-[#1E2329]"></div>
      </div>
      
      {/* Ripple effect */}
      {isAnimating && (
        <div className="absolute inset-0 rounded-lg bg-[#02C076] opacity-30 animate-ping"></div>
      )}
    </button>
  );
};

export default CopyButton;